import React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { toAbsoluteAssetUrl } from "../lib/api";

const withCacheBust = (src, bust) => {
  if (!src) return src;
  if (!bust) return src;
  // Blob/data URLs are not valid with arbitrary query strings (breaks preview uploads).
  if (/^(blob:|data:)/i.test(src)) return src;
  const join = src.includes("?") ? "&" : "?";
  return `${src}${join}v=${encodeURIComponent(bust)}`;
};

const initials = (name) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "—";
  const first = parts[0]?.[0] || "";
  const last = (parts.length > 1 ? parts[parts.length - 1]?.[0] : "") || "";
  return (first + last).toUpperCase();
};

export function AppAvatar({ name, src, size = 24, rounded = "full", className }) {
  const radius = rounded === "md" ? "rounded-md" : "rounded-full";
  const bust =
    typeof window !== "undefined"
      ? (localStorage.getItem("taistu_avatar_bust") || localStorage.getItem("taistu_asset_bust") || "")
      : "";
  const srcWithBust = src ? withCacheBust(toAbsoluteAssetUrl(src), bust) : null;
  return (
    <Avatar
      className={`${radius} border border-[var(--t-border)] bg-[var(--t-bg)] ${className || ""}`}
      style={{ width: size, height: size }}
    >
      {srcWithBust ? (
        <AvatarImage
          key={srcWithBust}
          src={srcWithBust}
          alt={name || "avatar"}
          className="aspect-square h-full w-full object-cover"
        />
      ) : null}
      <AvatarFallback className="bg-[var(--t-bg)] text-[10px] tracking-widest text-[var(--t-meta)]">
        {initials(name)}
      </AvatarFallback>
    </Avatar>
  );
}

export function AvatarStack({ people, size = 24, max = 3 }) {
  const shown = (people || []).slice(0, max);
  const rest = Math.max(0, (people || []).length - shown.length);
  return (
    <div className="flex items-center">
      {shown.map((p, idx) => (
        <div key={p.key || idx} className={idx === 0 ? "" : "-ml-2"}>
          <AppAvatar name={p.name} src={p.src} size={size} />
        </div>
      ))}
      {rest > 0 && (
        <div className="-ml-2">
          <AppAvatar name={`+${rest}`} src={null} size={size} className="bg-[var(--t-bg)]" />
        </div>
      )}
    </div>
  );
}

